import { LOCATION_FIX_RECEIVED, CLEAR_LOCATION_FIX } from "./actionTypes";

/**
 * Session-scoped location store — DELIBERATELY NOT PERSISTED.
 *
 * Not listed in the root `persistConfig.whitelist` (`["auth", "customer"]`, see
 * `src/store/index.js`), so it neither hydrates nor dehydrates. That is the
 * whole point of the slice: anything read out of it was observed by the running
 * process, never restored from disk.
 *
 * This replaced `auth.coords`, which was persisted (root whitelist plus
 * `authPersistConfig`, blacklisting only `face_capture`) and carried no
 * timestamp — so on a cold start it rehydrated with the previous session's fix,
 * indistinguishable from a live one. That whole `LIVE_LOCATION` chain was
 * deleted once this slice took over. Do NOT reintroduce a persisted location
 * store.
 *
 * Holds only the most recent fix ("max last location"), from either source:
 *   - `"stream"` — movex.geo background tracking (`MovexGeo.onLocations`)
 *   - `"capture"` — a one-shot `captureCurrentPosition` taken by a screen
 *
 * Out-of-order fixes are dropped: a stream emission that arrives after a newer
 * capture must not overwrite it, so writes are guarded on `updated_at`.
 */

export const actions = {
  /** @param payload {{latitude, longitude, accuracy?, source?}} */
  locationFixReceived: ({ latitude, longitude, accuracy, source }) => ({
    type: LOCATION_FIX_RECEIVED,
    payload: {
      latitude,
      longitude,
      accuracy: accuracy ?? null,
      source: source ?? "stream",
      // Stamped at action-creation so the reducer stays pure.
      updated_at: new Date().toISOString(),
    },
  }),
  clearLocationFix: () => ({
    type: CLEAR_LOCATION_FIX,
  }),
};

const initialState = {
  lastFix: null,
};

const isNewer = (incoming, current) => {
  if (!current?.updated_at) return true;
  return new Date(incoming.updated_at) >= new Date(current.updated_at);
};

const reducer = (state = initialState, action) => {
  switch (action.type) {
    case LOCATION_FIX_RECEIVED: {
      const fix = action.payload;
      if (fix?.latitude == null || fix?.longitude == null) return state;
      if (!isNewer(fix, state.lastFix)) return state;
      return {
        ...state,
        lastFix: fix,
      };
    }
    case CLEAR_LOCATION_FIX: {
      return {
        ...state,
        lastFix: null,
      };
    }
    default:
      return state;
  }
};

reducer.initialState = initialState;
export default reducer;
